import React, { useCallback } from "react";
import { useEngagementPrefill } from "../../../hooks/useEngagementPrefill";
import { AIPrefillEngagementResponse } from "../../../types/compassAITypes";
import { MESSAGES } from "../../../messages";
import { InlinePrefillBase } from "./InlinePrefillBase";

// =============================================================================
// TYPES
// =============================================================================

export interface InlinePrefillProps {
  /** Company ID (required to enable prefill) */
  companyId: string | null;
  /** Callback when prefill completes successfully */
  onSuccess?: (result: AIPrefillEngagementResponse) => void;
  /** Custom class name */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Inline AI prefill component for the engagement record form.
 * Thin wrapper around InlinePrefillBase that wires up useEngagementPrefill.
 */
export const InlinePrefill: React.FC<InlinePrefillProps> = ({
  companyId,
  onSuccess,
  className,
}) => {
  const {
    uploadedFiles,
    addFile,
    removeFile,
    startPrefill,
    reset,
    isUploading,
    isExtracting,
    canAddMoreFiles,
  } = useEngagementPrefill();

  const handleExtract = useCallback(
    async (prompt: string): Promise<AIPrefillEngagementResponse | null> => {
      if (!companyId) return null;
      const result = await startPrefill(companyId, prompt);
      if (result && onSuccess) {
        onSuccess(result);
      }
      return result;
    },
    [companyId, startPrefill, onSuccess],
  );

  return (
    <InlinePrefillBase
      isVisible={!!companyId}
      extractingLabel={
        MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.DESCRIPTION.FILLING
      }
      textPlaceholder="Paste meeting notes, engagement details, or any relevant text..."
      onExtract={handleExtract}
      uploadedFiles={uploadedFiles}
      addFile={addFile}
      removeFile={removeFile}
      reset={reset}
      isUploading={isUploading}
      isExtracting={isExtracting}
      canAddMoreFiles={canAddMoreFiles}
      className={className}
    />
  );
};
